import { useEffect, useReducer } from 'react';
import useSWR from 'swr';
import { APIError, fetcher, FetcherError, HTTPError, ResponseWrapper } from '@tlx/astro-shared';
import {
    askForTaxcardsFromAltinn,
    CHECK_WAITING_TAXCARD_APPLICATIONS_URL,
    fetchTaxcardsFromAltinn,
    IS_LOGGED_IN_ALTINN_UNTIL,
    LAST_UPDATED_ORDER_ID_URL,
    LAST_UPDATED_URL,
    loginAltinnStep1RequestPin,
    loginAltinnStep2VerifyPin,
    loginToTestAltinn,
} from '@Page/SalaryTaxCard/hooks/useSalaryTaxCardForReal';
import { sleep } from '@Util/misc';

export enum AltinnNotification {
    FIRST_CHANGE = 1,
    NO_CHANGE = 3,
}

export enum TaxcardStatusCodes {
    //AltinnTaxDeductionCard is the real list
    OK = 'skattekortopplysningerOK', //GREEN
    WORK_PERMIT_UNCLEAR = 'vurderArbeidstillatelse', //ORANGE
    D_NUM_EXPIRED_PERS_NUM_OK = 'utgaattDnummerSkattekortForFoedselsnummerErLevert', //ORANGE
    STATUS_MANUAL = 'manual', //ORANGE
    INVALID_ORGNO = 'ugyldigOrganisasjonsnummer', //RED
    INVALID_PERS_D_NUM = 'ugyldigFoedselsEllerDnummer', //RED
    NO_DEDUCTION = 'ikkeTrekkplikt', //GREY
    NO_TAXCARD = 'ikkeSkattekort', //GREY
}

type AltinnActionTaxcardsGeneral = {
    type: 'INIT' | 'LOGIN_TO_TEST_ALTINN';
};

type AltinnActionHasAppliedForTaxcards = {
    type: 'HAS_APPLIED_FOR_TAXCARDS';
};

type AltinnActionHasLoggedInAction = {
    type: 'HAS_LOGGEDIN_ALTINN';
    informSwrFunc: () => void;
};

type AltinnActionReceivedTaxcards = {
    type: 'HAS_RECEIVED_TAXCARDS';
    numCardsReceived: number;
    flushSwrCacheCardsWaitingFunc: (x: ResponseWrapper<boolean>) => void;
};

// This type, and many of the definitions below, are exported just for convenience in tests
export type AltinnActionStartApplicationProcess = {
    type: 'START_APPLICATION_PROCESS';
    applyForTheseEmployeeIds: string;
    selectedContactPersonId: number;
    selectedContactEmail: string;
    selectedYear: number;
    selectedPhoneNumber: string;
    selectedCountryCodeId: number;
    selectedNotification: string;
};

export type AltinnActionSetError = {
    type: 'SET_ERROR';
    error: Error;
};

export type Success = {
    area: string;
    message: string[];
};

export type AltinnActionSetSuccess = {
    type: 'SET_SUCCESS';
    success: Success;
};

export type AltinnActionLoginRequestPin = {
    type: 'LOGIN_TO_ALTINN_REQUEST_PIN';
    reportingCompanyId: number;
    personalIdNumber: string;
    personalAltinnPassword: string;
};

export type AltinnActionLoginVerifyPin = {
    type: 'LOGIN_TO_ALTINN_VERIFY_PIN';
    reportingCompanyId: number;
    personalIdNumber: string;
    personalAltinnPassword: string;
    pin: string;
};

type AltinnAction =
    | AltinnActionTaxcardsGeneral
    | AltinnActionLoginRequestPin
    | AltinnActionLoginVerifyPin
    | AltinnActionReceivedTaxcards
    | AltinnActionStartApplicationProcess
    | AltinnActionSetError
    | AltinnActionHasLoggedInAction
    | AltinnActionHasAppliedForTaxcards
    | AltinnActionSetSuccess;

export interface AltinnData {
    lastUpdated: Date | null;
    lastUpdatedOrderId?: number | undefined;
    hasAppliedForTaxcards: boolean;
    numCardsReceived: number;
    applicationProcessOngoing: boolean;
    isLoggedInUntil: Date | null;
    isOlderWaitingTaxcardApplications: boolean;
    isWaitingForAltinn: boolean;
    receivedCards: boolean;
    receivedNoCardsMustRetryFetch: boolean;
    error?: Error;
    success: Success[];
    dispatch?: (x: AltinnAction) => void;
    hasRequestedPin?: AltinnActionLoginRequestPin;
    hasRequestedLogin?: AltinnActionLoginVerifyPin;
}

export interface AltinnState {
    hasAppliedForTaxcards: boolean;
    numCardsReceived: number;
    applicationProcessOngoing: boolean;
    error?: Error;
    success: Success[];
    hasRequestedPin?: AltinnActionLoginRequestPin;
    hasRequestedLogin?: AltinnActionLoginVerifyPin;
    hasRequestedTestAltinnLogin?: boolean;
    applicationArgs: AltinnActionStartApplicationProcess | undefined;
}

const NUM_CARDS_INITIAL = -2;
const NUM_CARDS_NONE_RECEIVED = -1;

export const initialState: AltinnState = {
    hasAppliedForTaxcards: false,
    numCardsReceived: NUM_CARDS_INITIAL,
    applicationProcessOngoing: false,
    applicationArgs: undefined,
    error: undefined, //KGFIXME its optional so dont think it needs to be set
    success: [],
};

// Exported just to be able to run tests
export function reducer(state: AltinnState, action: AltinnAction): AltinnState {
    switch (action.type) {
        case 'HAS_APPLIED_FOR_TAXCARDS':
            return {
                ...initialState,
                hasAppliedForTaxcards: true,
            };
        case 'HAS_RECEIVED_TAXCARDS':
            // This is to prevent a subtle bug where the old swr value (true in the error case) is alive a little too long,
            // so that a fetch process is incorrectly started upon revisiting the page.  See TRIP-24877 [Kristoffer Kvello, 2022-06-21]
            if (action.numCardsReceived >= 0) {
                action.flushSwrCacheCardsWaitingFunc({ value: false });
            }
            return {
                ...initialState,
                hasAppliedForTaxcards: action.numCardsReceived === NUM_CARDS_NONE_RECEIVED,
                numCardsReceived: action.numCardsReceived,
            };
        case 'START_APPLICATION_PROCESS':
            return {
                ...initialState,
                applicationProcessOngoing: true,
                applicationArgs: action,
            };
        case 'LOGIN_TO_TEST_ALTINN':
            return { ...state, hasRequestedTestAltinnLogin: true };
        case 'LOGIN_TO_ALTINN_REQUEST_PIN':
            return { ...state, hasRequestedPin: action };
        case 'LOGIN_TO_ALTINN_VERIFY_PIN':
            return { ...state, hasRequestedLogin: action };
        case 'HAS_LOGGEDIN_ALTINN':
            action.informSwrFunc();
            return { ...initialState };
        case 'SET_ERROR':
            return { ...initialState, error: action.error };
        case 'SET_SUCCESS':
            return {
                ...initialState,
                success: [...state.success, action.success],
            };
        case 'INIT':
            return { ...initialState };
    }
}

export function useAltinnHandler(): AltinnData {
    // TRIP-24533: handle the  case where altinn does not answer a taxcard application before the user navigates away.
    const {
        data: isLoggedInUntilSWR,
        // error: errorLogincheck,
        mutate: mutateisLoggedInUntil,
    } = useSWR<ResponseWrapper<string>>(IS_LOGGED_IN_ALTINN_UNTIL);
    const isLoggedInUntil: Date | null = isLoggedInUntilSWR?.value ? new Date(isLoggedInUntilSWR.value) : null;
    const isLoggedIn: boolean = !!isLoggedInUntil;
    const {
        data: isOlderWaitingTaxcardApplicationsSWR,
        // In a specific case we need to immediately inform swr that the waiting cards are handled ok now. [Kristoffer Kvello, 2022-06-21]
        mutate: waitingTaxcardsFixed,
    } = useSWR<ResponseWrapper<boolean>>(CHECK_WAITING_TAXCARD_APPLICATIONS_URL);
    const isOlderWaitingTaxcardApplications: boolean = isOlderWaitingTaxcardApplicationsSWR
        ? isOlderWaitingTaxcardApplicationsSWR.value
        : false;

    const { data: lastUpdatedSWR } = useSWR<ResponseWrapper<string>>(LAST_UPDATED_URL);
    const lastUpdated: Date | null = lastUpdatedSWR?.value ? new Date(lastUpdatedSWR.value) : null;

    const [state, dispatch] = useReducer(reducer, initialState);

    // The last type in the specialisation list is the type of the key argument to the fetcher.
    const { data: lastUpdatedOrderIdSWR } = useSWR<ResponseWrapper<number>, FetcherError, [string, number]>(
        [LAST_UPDATED_ORDER_ID_URL, state.numCardsReceived],
        (key) => fetcher(key[0]),
    );

    const lastUpdatedOrderId: number | undefined = lastUpdatedOrderIdSWR?.value ?? undefined;

    function dispatchSuccess(success: Success) {
        dispatch({
            type: 'SET_SUCCESS',
            success: success,
        });
    }

    function dispatchError(err: unknown) {
        const errr = err instanceof Error ? err : new Error('Unknown error: ' + err);
        // This is either an APIError or an HTTPError (always?), from createAPIResponse. Both ValidationException and ApplicationException are APIError. But AE has no message in APIError.
        let error: Error = errr;
        if (errr.name === 'APIError') {
            error = err as APIError;
        } else if (errr.name === 'HTTPError') {
            error = err as HTTPError;
        }
        dispatch({ type: 'SET_ERROR', error: error });
    }

    // LOGIN TO TEST-ALTINN!
    useEffect(() => {
        if (state.hasRequestedTestAltinnLogin) {
            loginToTestAltinn()
                .then(() =>
                    dispatch({
                        type: 'HAS_LOGGEDIN_ALTINN',
                        informSwrFunc: mutateisLoggedInUntil,
                    }),
                )
                .catch((err) => dispatchError(err));
        }
    }, [state.hasRequestedTestAltinnLogin, mutateisLoggedInUntil]);

    //Step1 useEffect
    useEffect(() => {
        if (state.hasRequestedPin) {
            loginAltinnStep1RequestPin(
                state.hasRequestedPin.reportingCompanyId,
                state.hasRequestedPin.personalIdNumber,
                state.hasRequestedPin.personalAltinnPassword,
            )
                .then((message) => {
                    dispatchSuccess({
                        area: state.hasRequestedPin?.type ?? '',
                        message: message,
                    });
                })
                .catch((error) => {
                    dispatchError(error);
                });
        }
    }, [state.hasRequestedPin]);

    //Step2 useEffect
    useEffect(() => {
        if (state.hasRequestedLogin) {
            loginAltinnStep2VerifyPin(
                state.hasRequestedLogin.reportingCompanyId,
                state.hasRequestedLogin.personalIdNumber,
                state.hasRequestedLogin.personalAltinnPassword,
                state.hasRequestedLogin.pin,
            )
                .then(() => {
                    dispatch({
                        type: 'HAS_LOGGEDIN_ALTINN',
                        informSwrFunc: mutateisLoggedInUntil,
                    });
                })
                .catch((err) => {
                    dispatchError(err);
                });
        }
    }, [state.hasRequestedLogin, mutateisLoggedInUntil]);

    // useEffect that applies for taxcards when applicationProcessOngoing is true
    useEffect(() => {
        if (state.applicationProcessOngoing && state.applicationArgs) {
            askForTaxcardsFromAltinn(
                state.applicationArgs.applyForTheseEmployeeIds,
                state.applicationArgs.selectedContactPersonId,
                state.applicationArgs.selectedContactEmail,
                state.applicationArgs.selectedYear,
                state.applicationArgs.selectedPhoneNumber,
                state.applicationArgs.selectedCountryCodeId,
                state.applicationArgs.selectedNotification,
            )
                .then(() => {
                    dispatch({
                        type: 'HAS_APPLIED_FOR_TAXCARDS',
                    });
                })
                .catch((err) => dispatchError(err));
        }
    }, [state.applicationProcessOngoing, state.applicationArgs]);

    useEffect(() => {
        if (isLoggedIn && isOlderWaitingTaxcardApplications) {
            dispatch({ type: 'HAS_APPLIED_FOR_TAXCARDS' });
        }
    }, [isLoggedIn, isOlderWaitingTaxcardApplications]);

    useEffect(() => {
        async function fetchTaxcards(swrFunc: (x: ResponseWrapper<boolean>) => void) {
            const numTries = 5;
            let numTried = 0;
            let sleepTime = 5000;
            const sleepIncrement = 2000;
            let numReceived = NUM_CARDS_NONE_RECEIVED;

            while (numReceived === NUM_CARDS_NONE_RECEIVED && numTried < numTries) {
                await sleep(sleepTime);
                try {
                    const val: string = await fetchTaxcardsFromAltinn();
                    numTried++;
                    numReceived = parseInt(val);
                    if (numReceived > NUM_CARDS_NONE_RECEIVED || numTried === numTries) {
                        dispatch({
                            type: 'HAS_RECEIVED_TAXCARDS', // it looks a little dumb that we dispatch this message even when none are received and we give up, but that situation is handled in the reducer. [Kristoffer Kvello, 2022-06-30]
                            numCardsReceived: numReceived,
                            // This is to prevent a subtle bug where the old swr value (true in the error case) is alive a little too long,
                            // so that a fetch process is incorrectly started upon revisiting the page.  See the TRIP-24877 [Kristoffer Kvello, 2022-06-21]
                            flushSwrCacheCardsWaitingFunc: swrFunc,
                        });
                    }
                } catch (e) {
                    dispatchError(e);
                    numTried = numTries; // abort the try-fetch loop in this case - it will usually be one of the mysterious altinn errors (or errrors) [Kristoffer Kvello, 2022-08-19]
                }
                sleepTime += sleepIncrement;
            }
        }

        if (state.hasAppliedForTaxcards && state.numCardsReceived === NUM_CARDS_INITIAL && isLoggedIn) {
            fetchTaxcards(waitingTaxcardsFixed).then();
        }
    }, [state.hasAppliedForTaxcards, state.numCardsReceived, isLoggedIn, waitingTaxcardsFixed]);

    return {
        lastUpdated,
        lastUpdatedOrderId,
        hasAppliedForTaxcards: state.hasAppliedForTaxcards,
        numCardsReceived: state.numCardsReceived,
        applicationProcessOngoing: state.applicationProcessOngoing,
        isLoggedInUntil,
        isOlderWaitingTaxcardApplications,
        isWaitingForAltinn:
            state.applicationProcessOngoing ||
            (state.hasAppliedForTaxcards && state.numCardsReceived === NUM_CARDS_INITIAL),
        receivedCards: state.numCardsReceived >= 0 && !state.applicationProcessOngoing,
        receivedNoCardsMustRetryFetch:
            state.numCardsReceived === NUM_CARDS_NONE_RECEIVED && state.hasAppliedForTaxcards,
        error: state.error,
        success: state.success,
        dispatch,
        hasRequestedPin: state.hasRequestedPin,
        hasRequestedLogin: state.hasRequestedLogin,
    };
}
